import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { User, UserWallet } from "../../types";
import {
  MdAccountBalanceWallet,
  MdSearch,
  MdLinkOff,
  MdDelete,
  MdRefresh,
  MdPerson,
  MdContentCopy,
  MdCheckCircle,
  MdVisibility,
  MdVisibilityOff,
  MdSecurity,
} from "react-icons/md";

const UserWalletManagement = () => {
  const { addAlert } = useAlert();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [userWallets, setUserWallets] = useState<UserWallet[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedAddress, setCopiedAddress] = useState("");
  const [showPhrase, setShowPhrase] = useState<{ [key: number]: boolean }>({});
  const [copiedPhrase, setCopiedPhrase] = useState<{ [key: number]: boolean }>(
    {}
  );

  const WalletIcon = MdAccountBalanceWallet as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const SearchIcon = MdSearch as React.FC<React.SVGProps<SVGSVGElement>>;
  const DisconnectIcon = MdLinkOff as React.FC<React.SVGProps<SVGSVGElement>>;
  const DeleteIcon = MdDelete as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const PersonIcon = MdPerson as React.FC<React.SVGProps<SVGSVGElement>>;
  const CopyIcon = MdContentCopy as React.FC<React.SVGProps<SVGSVGElement>>;
  const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;
  const VisibilityIcon = MdVisibility as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const VisibilityOffIcon = MdVisibilityOff as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const SecurityIcon = MdSecurity as React.FC<React.SVGProps<SVGSVGElement>>;

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      const [usersResponse, walletsResponse] = await Promise.all([
        adminAPI.getAllUsers(),
        adminAPI.getAllUserWallets(),
      ]);

      console.log("Users:", usersResponse.data);
      console.log("Wallets:", walletsResponse.data);

      // Ensure we have arrays even if the response is null/undefined
      const usersData = usersResponse.data || [];
      const walletsData = walletsResponse.data || [];

      // Process wallets to ensure user_id is number
      const processedWallets = walletsData.map((wallet: any) => ({
        ...wallet,
        user_id: Number(wallet.user_id), // Convert string to number
      }));

      setUsers(usersData);
      setFilteredUsers(usersData);
      setUserWallets(processedWallets);

      // Debug: Log user-wallet relationships
      console.log("=== USER-WALLET RELATIONSHIPS ===");
      usersData.forEach((user: User) => {
        const userWalletsCount = processedWallets.filter(
          (wallet: UserWallet) => wallet.user_id === user.id
        ).length;
        console.log(
          `User ${user.first_name} ${user.last_name} (ID: ${user.id}): ${userWalletsCount} wallets`
        );
      });

      // Debug: Log all wallets with their user_ids
      console.log("=== ALL WALLETS ===");
      processedWallets.forEach((wallet: UserWallet) => {
        console.log(
          `Wallet ID: ${wallet.id}, User ID: ${
            wallet.user_id
          } (type: ${typeof wallet.user_id})`
        );
      });
    } catch (error: any) {
      console.error("Error fetching data:", error);
      addAlert({ type: "error", message: "Failed to fetch data" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleDisconnectWallet = async (walletId: number) => {
    try {
      await adminAPI.disconnectUserWallet(walletId);
      addAlert({
        type: "success",
        message: "Wallet disconnected successfully",
      });
      fetchAllData();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to disconnect wallet",
      });
    }
  };

  const handleDeleteWallet = async (walletId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this wallet? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await adminAPI.deleteUserWallet(walletId);
      addAlert({ type: "success", message: "Wallet deleted successfully" });
      fetchAllData();
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to delete wallet",
      });
    }
  };

  const handleViewUserWallets = (user: User) => {
    setSelectedUser(user);
  };

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    addAlert({ type: "success", message: "Address copied to clipboard!" });
    setTimeout(() => setCopiedAddress(""), 2000);
  };

  const handleTogglePhrase = (walletId: number) => {
    setShowPhrase((prev) => ({
      ...prev,
      [walletId]: !prev[walletId],
    }));
  };

  const handleCopyPhrase = (walletId: number, phrase: string) => {
    navigator.clipboard.writeText(phrase);
    setCopiedPhrase((prev) => ({
      ...prev,
      [walletId]: true,
    }));
    addAlert({
      type: "success",
      message: "Backup phrase copied to clipboard!",
    });
    setTimeout(() => {
      setCopiedPhrase((prev) => ({
        ...prev,
        [walletId]: false,
      }));
    }, 2000);
  };

  const getUserWallets = (userId: number): UserWallet[] => {
    const wallets = userWallets.filter((wallet) => wallet.user_id === userId);
    console.log(`Wallets for user ${userId}:`, wallets);
    return wallets;
  };

  const getWalletIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      metamask: "🦊",
      trustwallet: "🔷",
      coinbase: "🏦",
      phantom: "👻",
      walletconnect: "🔗",
      exodus: "🌌",
      atomic: "⚛️",
      myetherwallet: "🔶",
      solflare: "🔥",
      other: "💼",
    };
    return icons[type] || "💼";
  };

  const getStatusBadge = (isConnected: boolean) => {
    return isConnected ? (
      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full dark:bg-green-900 dark:text-green-200">
        Connected
      </span>
    ) : (
      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full dark:bg-red-900 dark:text-red-200">
        Disconnected
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate statistics correctly
  const totalConnectedWallets = userWallets.filter(
    (wallet) => wallet.is_connected
  ).length;

  const totalUsersWithWallets = new Set(
    userWallets.map((wallet) => wallet.user_id)
  ).size;

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            User Wallet Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Manage and monitor user wallet connections across the platform.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchAllData}
            disabled={isLoading}
            className={`${GlobalStyles.buttonIconOutline} ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <RefreshIcon
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-primary`}>
            {users.length}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Total Users
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-green-600`}>
            {totalUsersWithWallets}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Users with Wallets
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-blue-600`}>
            {userWallets.length}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Total Wallets
          </div>
        </div>
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className={`${CommonClasses.textXl} font-bold text-yellow-600`}>
            {totalConnectedWallets}
          </div>
          <div className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
            Connected Now
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={`${GlobalStyles.sectionWrap} mb-6`}>
        <div className={GlobalStyles.inputWrapper}>
          <SearchIcon className={GlobalStyles.inputIcon} />
          <input
            type="text"
            placeholder="Search users by name, email, or username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={GlobalStyles.input}
          />
        </div>
      </div>

      {/* Users Table */}
      <div className={GlobalStyles.tableContainer}>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>
              Loading user data...
            </p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <PersonIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>No users found</p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "No users registered yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead className={GlobalStyles.tableHeader}>
                <tr>
                  <th className={GlobalStyles.tableHeaderCell}>User</th>
                  <th className={GlobalStyles.tableHeaderCell}>Wallets</th>
                  <th className={GlobalStyles.tableHeaderCell}>Connected</th>
                  <th className={GlobalStyles.tableHeaderCell}>
                    Last Activity
                  </th>
                  <th className={GlobalStyles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody className={GlobalStyles.tableBody}>
                {filteredUsers.map((user) => {
                  const userWalletsList = getUserWallets(user.id);
                  const connectedWallets = userWalletsList.filter(
                    (w) => w.is_connected
                  );
                  const lastUsedWallet = userWalletsList
                    .filter((w) => w.last_used_at)
                    .sort(
                      (a, b) =>
                        new Date(b.last_used_at).getTime() -
                        new Date(a.last_used_at).getTime()
                    )[0];

                  return (
                    <tr key={user.id} className={GlobalStyles.tableRow}>
                      <td className={GlobalStyles.tableCell}>
                        <div className={`${CommonClasses.flexStart} gap-3`}>
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {user.first_name[0]}
                              {user.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <div
                              className={`font-medium ${CommonClasses.textGrayStrong}`}
                            >
                              {user.first_name} {user.last_name}
                            </div>
                            <div
                              className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                            >
                              @{user.username}
                            </div>
                            <div
                              className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                            >
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className={`${CommonClasses.flexStart} gap-2`}>
                          <WalletIcon className="w-4 h-4 text-gray-500" />
                          <span className="font-medium">
                            {userWalletsList.length}
                          </span>
                          <span
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            wallets
                          </span>
                        </div>
                        {userWalletsList.length > 0 && (
                          <div
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                          >
                            {connectedWallets.length} connected
                          </div>
                        )}
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        {connectedWallets.length > 0 ? (
                          <div className="space-y-1">
                            {getStatusBadge(true)}
                            <div
                              className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                            >
                              {connectedWallets.length} active
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            No connected wallets
                          </span>
                        )}
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        {lastUsedWallet ? (
                          <div>
                            <div
                              className={`${CommonClasses.textSm} ${CommonClasses.textGrayStrong}`}
                            >
                              {formatDate(lastUsedWallet.last_used_at)}
                            </div>
                            <div
                              className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                            >
                              {lastUsedWallet.wallet_type}
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                          >
                            Never
                          </span>
                        )}
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <button
                          onClick={() => handleViewUserWallets(user)}
                          disabled={userWalletsList.length === 0}
                          className={`${GlobalStyles.buttonPrimary} !text-xs ${
                            userWalletsList.length === 0
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <WalletIcon className="w-3 h-3 mr-1" />
                          {userWalletsList.length === 0
                            ? "No Wallets"
                            : "View Wallets"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Wallets Modal */}
      {selectedUser && (
        <div className={GlobalStyles.modalOverlay}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md w-full max-w-4xl max-h-[90vh] overflow-y-auto my-8">
            <div className="p-5 border-b border-gray-300 dark:border-gray-700">
              <div className={`${CommonClasses.flexBetween}`}>
                <div>
                  <h2
                    className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
                  >
                    {selectedUser.first_name}'s Wallets
                  </h2>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
                  >
                    Manage wallet connections for {selectedUser.first_name}{" "}
                    {selectedUser.last_name}
                  </p>
                  <p
                    className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                  >
                    User ID: {selectedUser.id} | Email: {selectedUser.email}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className={`${GlobalStyles.buttonIconOutline}`}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {getUserWallets(selectedUser.id).length === 0 ? (
                <div className="text-center py-8">
                  <WalletIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className={`${CommonClasses.textGray} mb-2`}>
                    No wallets connected
                  </p>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    This user hasn't connected any wallets yet
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getUserWallets(selectedUser.id).map((wallet) => (
                    <div
                      key={wallet.id}
                      className={`${GlobalStyles.sectionWrap}`}
                    >
                      <div className={`${CommonClasses.flexBetween} mb-4`}>
                        <div className={`${CommonClasses.flexStart} gap-3`}>
                          <span className="text-2xl">
                            {getWalletIcon(wallet.wallet_type)}
                          </span>
                          <div>
                            <div
                              className={`font-semibold ${CommonClasses.textGrayStrong}`}
                            >
                              {wallet.wallet_name || wallet.wallet_type}
                            </div>
                            <div
                              className={`${CommonClasses.textSm} ${CommonClasses.textGray} capitalize`}
                            >
                              {wallet.wallet_type}
                            </div>
                            <div
                              className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                            >
                              Wallet ID: {wallet.id}
                            </div>
                          </div>
                        </div>
                        {getStatusBadge(wallet.is_connected)}
                      </div>

                      {/* Backup Phrase Section */}
                      <div className="mb-4">
                        <label className={GlobalStyles.label}>
                          Backup Phrase
                        </label>
                        <div className="relative">
                          <input
                            type={showPhrase[wallet.id] ? "text" : "password"}
                            value={
                              wallet.backup_phrase || "No phrase available"
                            }
                            readOnly
                            className={`${GlobalStyles.input} bg-gray-50 dark:bg-gray-700 font-mono text-sm pr-20`}
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
                            <button
                              onClick={() => handleTogglePhrase(wallet.id)}
                              className={`${GlobalStyles.buttonIconOutline}`}
                              title={
                                showPhrase[wallet.id]
                                  ? "Hide phrase"
                                  : "Show phrase"
                              }
                            >
                              {showPhrase[wallet.id] ? (
                                <VisibilityOffIcon className="w-4 h-4" />
                              ) : (
                                <VisibilityIcon className="w-4 h-4" />
                              )}
                            </button>
                            {wallet.backup_phrase && (
                              <button
                                onClick={() =>
                                  handleCopyPhrase(
                                    wallet.id,
                                    wallet.backup_phrase!
                                  )
                                }
                                className={`${GlobalStyles.buttonIconOutline}`}
                                title="Copy phrase"
                              >
                                {copiedPhrase[wallet.id] ? (
                                  <CheckIcon className="w-4 h-4 text-green-500" />
                                ) : (
                                  <CopyIcon className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        {!wallet.backup_phrase && (
                          <p
                            className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                          >
                            No backup phrase stored for this wallet
                          </p>
                        )}
                      </div>

                      {wallet.wallet_address && (
                        <div className="mb-4">
                          <label className={GlobalStyles.label}>
                            Wallet Address
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={wallet.wallet_address}
                              readOnly
                              className={`${GlobalStyles.input} bg-gray-50 dark:bg-gray-700 font-mono text-sm pr-10`}
                            />
                            <button
                              onClick={() =>
                                handleCopyAddress(wallet.wallet_address!)
                              }
                              className={`${GlobalStyles.buttonIconOutline} absolute right-2 top-1/2 transform -translate-y-1/2`}
                            >
                              {copiedAddress === wallet.wallet_address ? (
                                <CheckIcon className="w-4 h-4 text-green-500" />
                              ) : (
                                <CopyIcon className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div>
                          <span className={CommonClasses.textGray}>
                            Connected:
                          </span>
                          <span className="ml-1 font-medium">
                            {formatDate(wallet.connected_at)}
                          </span>
                        </div>
                        <div>
                          <span className={CommonClasses.textGray}>
                            Last Used:
                          </span>
                          <span className="ml-1 font-medium">
                            {formatDate(wallet.last_used_at)}
                          </span>
                        </div>
                        <div>
                          <span className={CommonClasses.textGray}>
                            Verified:
                          </span>
                          <span
                            className={`ml-1 font-medium ${
                              wallet.is_verified
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {wallet.is_verified ? "Yes" : "No"}
                          </span>
                        </div>
                        <div>
                          <span className={CommonClasses.textGray}>
                            Primary:
                          </span>
                          <span
                            className={`ml-1 font-medium ${
                              wallet.is_primary
                                ? "text-green-600"
                                : "text-gray-600"
                            }`}
                          >
                            {wallet.is_primary ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {wallet.is_connected ? (
                          <button
                            onClick={() => handleDisconnectWallet(wallet.id)}
                            className={`${GlobalStyles.buttonOutline} flex-1 !text-xs`}
                          >
                            <DisconnectIcon className="w-3 h-3 mr-1" />
                            Disconnect
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleDeleteWallet(wallet.id)}
                          className={`${GlobalStyles.buttonIconDanger} flex-1`}
                        >
                          <DeleteIcon className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Security Notice */}
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <SecurityIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div>
                    <p
                      className={`${CommonClasses.textSm} font-medium text-red-800 dark:text-red-200`}
                    >
                      ⚠️ Extreme Security Warning
                    </p>
                    <p
                      className={`${CommonClasses.textXs} text-red-700 dark:text-red-300 mt-1`}
                    >
                      • Backup phrases provide full access to user's
                      cryptocurrency funds
                      <br />
                      • Never share these phrases with anyone
                      <br />
                      • Access should be limited to authorized administrators
                      only
                      <br />
                      • All access to backup phrases is logged and monitored
                      <br />• Treat this information with the highest level of
                      security
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserWalletManagement;
